import { skyProjectileEnergy } from '../../../packages/domain/src/81-sky-projectiles.mjs';

/** Shared original bolt geometry, in metres. Five earned tiers have different
 * silhouettes; piercing rails only appear on bolts carrying actual penetration.
 * Meshes never steer a projectile or infer collision from the luminous trail. */
export function createSkyProjectileArt(T, own, additive, reduced) {
  const colors = ['#69d8ff', '#6aff9b', '#ffcc54', '#bc89ff', '#ff8cdd'];
  const cores = colors.map((color) => additive(color));
  const halos = colors.map((color) => additive(color, 0.25));
  const needle = own(new T.OctahedronGeometry(1));
  const capsule = own(new T.IcosahedronGeometry(1, 1));
  const collar = own(new T.TorusGeometry(1, 0.13, 5, 16));
  const rail = own(new T.BoxGeometry(1, 1, 1));
  const forward = new T.Vector3(0, 0, -1),
    direction = new T.Vector3();
  const tierOf = (item) =>
    Number.isInteger(item.weaponLevel) ? Math.max(0, Math.min(4, item.weaponLevel)) : 0;
  function create(item) {
    const group = new T.Group(),
      tier = tierOf(item);
    const escort = item.source === 'drone';
    group.name = escort
      ? 'Escort dart'
      : ['Ion needle', 'Pulse capsule', 'Solar sabot', 'Rail lance', 'Prismatic lance'][tier];
    group.userData.weaponLevel = tier;
    group.userData.piercingRails = [];
    const add = (shape, material, scale, position = [0, 0, 0], name = '') => {
      const mesh = new T.Mesh(shape, material);
      mesh.name = name;
      mesh.scale.set(...scale);
      mesh.position.set(...position);
      group.add(mesh);
      return mesh;
    };
    add(tier === 1 ? capsule : needle, cores[tier], [0.055, 0.055, escort ? 0.5 : 0.8]);
    add(capsule, halos[tier], [0.1, 0.1, escort ? 0.7 : 1.05], [0, 0, 0.2]);
    if (!escort) {
      if (tier === 1) add(collar, cores[tier], [0.14, 0.14, 0.14], [0, 0, 0.15]);
      if (tier === 2) {
        for (const side of [-1, 1])
          add(needle, cores[tier], [0.12, 0.03, 0.42], [side * 0.1, 0, 0.25]);
      }
      if (tier === 3) {
        for (const z of [-0.32, 0.36]) add(collar, halos[tier], [0.12, 0.12, 0.12], [0, 0, z]);
      }
      if (tier >= 3 || (item.pierceRemaining ?? 0) > 0) {
        for (const side of [-1, 1]) {
          const fin = add(
            rail,
            cores[tier],
            [0.024, 0.024, 1.7],
            [side * 0.11, 0, 0.2],
            'Penetration rail',
          );
          group.userData.piercingRails.push(fin);
        }
        if (tier === 4) {
          const crown = add(collar, cores[tier], [0.15, 0.15, 0.15], [0, 0, -0.35]);
          crown.rotation.z = Math.PI / 4;
          add(needle, halos[tier], [0.035, 0.17, 1.35], [0, 0, 0.1]);
        }
      }
    }
    return group;
  }
  function update(group, item) {
    const tier = tierOf(item),
      energy = Math.sqrt(skyProjectileEnergy(item.distance));
    // Damage changes brightness/width within a readable ceiling; large upgrades
    // must not create a luminous hitbox wider than their actual precision path.
    const power = Math.min(2, 1 + Math.max(0, (item.damage ?? 1) - 1) * 0.3);
    group.scale.set(power * energy, power * energy, (1 + tier * 0.18) * energy);
    direction.set(item.vx ?? 0, item.vy ?? 0, item.vz ?? -(item.speed ?? 120)).normalize();
    group.quaternion.setFromUnitVectors(forward, direction);
    for (const part of group.userData.piercingRails ?? [])
      part.visible = (item.pierceRemaining ?? 0) > 0;
    // Spin only the sheath; the ballistic root remains aligned with velocity.
    group.children[1].rotation.z = reduced() ? 0 : (item.distance ?? 0) * 0.06;
  }
  return { create, update, cores };
}
